import { cn } from "../ClassName";

export type StandardButtonStyles =
  | "primary-blue"
  | "secondary-blue"
  | "gray"
  | "secondary-gray"
  | "primary-orange"
  | "primary-red"
  | "secondary-red-sonner"
  | "warning"
  | "green"
  | "gradient-purple";

interface StandardButtonProps {
  /**
   * @type string
   * @description Tailwind CSS string to override default styling
   */
  className?: string;
  /**
   * @type any
   * @description Any child components.
   */
  children?: any;
  /**
   * @type 'primary-blue' | 'secondary-blue' | 'gray' | 'warning'
   * @description The style of the button.
   */
  style: StandardButtonStyles;
  /**
   * @type (e) => void
   * @description Function to be called on clicking the button.
   */
  onClick: (e?: any) => void;
  /**
   * @type (e) => void
   * @description Function to be called on mouse down. Useful to call e.preventDefault()
   * so clicking the button does not blur (steal focus from) the currently focused element.
   */
  onMouseDown?: (e?: any) => void;
  /**
   * @description Pass this prop to render a disabled button.
   */
  disabled?: boolean;
  /**
   * @description Pass this prop to render a loading left icon when loading is true (will override iconLeft prop when true).
   */
  loading?: boolean;
  /**
   * @type String
   * @description Fontawesome icon string. Icon to be added on the left of the text.
   * @example "fas fa-check"
   */
  iconLeft?: string;
  /**
   * @type String
   * @description Fontawesome icon string. Icon to be added on the right of the text.
   * @example "fas fa-check"
   */
  iconRight?: string;
  /**
   * @type Element
   * @description Element for the tooltip, activated on hover.
   */
  tooltip?: React.ReactElement | null;
  /**
   * @type: String
   * @description: Target selector for Cypress tests.
   */
  dataCy?: string;
  /**
   * @type: String
   * @description: Target selector for Playwright tests.
   */
  dataTestId?: string;

  type?: "submit" | "reset" | "button";
}

/**
 * @description
 * Required Props: `style`, `onClick`
 *
 * Optional Props: `className`, `children`, `disabled`, `iconLeft`, `iconRight`, `tooltip`, `dataCy`
 */
export function StandardButton(props: StandardButtonProps) {
  const styleToClassName = {
    "primary-blue": "primary-blue-btn",
    "secondary-blue": "secondary-blue-btn",
    "secondary-gray": "secondary-gray-btn",
    "primary-orange":
      "tw-bg-orange-1 tw-text-white tw-border-none hover:tw-bg-orange-0",
    "primary-red": "tw-bg-red-1 tw-text-white tw-border-none hover:tw-bg-red-2",
    "secondary-red-sonner":
      "tw-border-[1px] tw-rounded-xl tw-border-solid tw-border-red-1 tw-bg-red-light-1 tw-px-[4px] tw-py-[2px] tw-text-red-1 hover:tw-bg-red-light-2 disabled:tw-border-red-light-2 disabled:tw-text-red-light-2",
    gray: "gray-btn",
    warning: "warning-btn",
    green: "green-btn",
    "gradient-purple": "tw-bg-gradient-to-r tw-from-blue-500 tw-to-purple-800",
  };

  return (
    <span className="tw-group">
      {props.tooltip && (
        <div className="tw-hidden group-hover:tw-block">{props.tooltip}</div>
      )}
      <button
        tabIndex={0}
        className={cn(
          "standard-btn",
          styleToClassName[props.style],
          props.className,
          props.className?.includes("tw-h-") ? "" : "tw-min-h-[36px]",
        )}
        onClick={(e) => props.onClick(e)}
        onMouseDown={props.onMouseDown}
        disabled={props.disabled}
        data-cy={props.dataCy !== undefined ? props.dataCy : null}
        data-testid={props.dataTestId}
        type={props.type}
      >
        {props.loading ? (
          <i className="fas fa-spinner fa-spin-pulse tw-mr-[8px]" />
        ) : props.iconLeft ? (
          <i className={`${props.iconLeft} tw-mr-[8px]`} />
        ) : null}
        {props.children}
        {props.iconRight && <i className={`${props.iconRight} tw-ml-[8px]`} />}
      </button>
    </span>
  );
}
